import { useSavedChartsStore } from '@/stores/savedChartsStore';
import { useAuthentication } from '@/composables/useAuthentication';
import { useChartNames } from '@/composables/useChartNames';
import { fetchCharts } from '@/composables/fetchCharts';
import { parseCharts } from '@/composables/parseCharts';
import { clearRecidsOfChartAndLoadSymbolOnlyChartBeingViewedIfSymbolHasChanged } from '@/composables/clearRecidsOfChartAndLoadSymbolOnlyChartBeingViewedIfSymbolHasChanged';
import { getChartListFromCache } from '@/composables/getChartListFromCache';
import { sortObjectsClosure } from '@/utils/sortObjectsClosure';
import { SimpleFrontendChart } from '@/interfaces/chart';

const loadChartsForChartListPromisesCache: Record<string, Promise<void>> = {};

async function _loadChartsForChartList(chartListRecid: number, chartListListNum: number) {
  const savedChartsStore = useSavedChartsStore();
  const { getChartNameAndTemporaryName } = useChartNames();

  savedChartsStore.$patch((state) => {
    const actualChartList = state.chartLists.find(cl => cl.recid === chartListRecid);

    if (actualChartList) {
      actualChartList.isAttemptingToLoadCharts = true;
    }
  });

  let charts: SimpleFrontendChart[] | null = null;

  try {
    const rawCharts = await fetchCharts(chartListListNum.toString());
    charts = parseCharts(rawCharts)
      .filter(c => c.chartListListNum === chartListListNum);

    charts.sort(sortObjectsClosure({
      propertyName: 'htmlSanitizedName',
      order: 'ascending'
    }));
    charts.forEach(c => clearRecidsOfChartAndLoadSymbolOnlyChartBeingViewedIfSymbolHasChanged(c));
    charts.forEach(c => {
      const { name, temporaryName } = getChartNameAndTemporaryName(c);

      c.name = name;
      c.temporaryName = temporaryName;
    });
  } catch (e) {
    window.sc3WorkbenchLogger.error(`Failed to load charts for ChartList with recid: ${chartListRecid}.`, e);
  }

  savedChartsStore.$patch((state) => {
    for (const chartList of state.chartLists) {
      if (chartList.recid !== chartListRecid) {
        chartList.charts = chartList.charts.filter(c1 => !charts?.find(c2 => c2.recid === c1.recid));
        continue;
      }

      if (Array.isArray(charts)) {
        chartList.charts = charts;
      }

      chartList.hasAttemptedToLoadCharts = true;
      chartList.hasFailedToLoadCharts = !Array.isArray(charts);
      chartList.isAttemptingToLoadCharts = false;
    }
  });
}

export async function loadChartsForChartList(chartListRecid?: number, chartListListNum?: number, forceUpdate?: boolean) { // TODO: if this is "one or the other", we should figure out a better way to handle this. Might need to go back to options object, or split into two functions.
  const { isLoggedIn } = useAuthentication();

  if (!isLoggedIn.value) {
    return;
  }

  const chartList = getChartListFromCache(chartListListNum, chartListRecid);

  if (!chartList) {
    window.sc3WorkbenchLogger.info(`Unable to load charts for ChartList - chartList not found in cache for recid: ${typeof chartListRecid} ${chartListRecid} and/or listNum: ${typeof chartListListNum} ${chartListListNum}.`);
    return;
  }

  if (!forceUpdate && chartList.hasAttemptedToLoadCharts) {
    return;
  }

  const chartListRecidString = `${chartList.recid}`;
  let promiseObject;
  if (chartListRecidString in loadChartsForChartListPromisesCache) {
    // there's already a request for this ChartList underway - await on the current
    // request's promise rather than kicking off a new request
    promiseObject = loadChartsForChartListPromisesCache[chartListRecidString];
  } else {
    promiseObject = _loadChartsForChartList(chartList.recid, chartList.listNum);

    // store promise in a cache so that additional loadChartsForChartList function calls
    // for this same ChartList that are made while the current network call is still
    // underway can await this promise rather than inefficiently making their own duplicate
    // network requests for the same data
    loadChartsForChartListPromisesCache[chartListRecidString] = promiseObject;

    promiseObject.then(() => {
      // remove the now-resolved promise from the cache
      delete loadChartsForChartListPromisesCache[chartListRecidString];
    });
  }

  await promiseObject;
}
